import _capitalize from 'hs-lodash/capitalize';
import _get from 'hs-lodash/get';
import _isNil from 'hs-lodash/isNil';
import _omitBy from 'hs-lodash/omitBy';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { PROPERTIES_LIMIT, PROPERTY_NAME_TO_REFERENCE_TYPE, propertyFieldTypes, propertyReferenceTypes } from '../../constants/properties';
import { pluralTypeToId, singularTypeToPlural } from '../../constants/types';
import { PreviewModalPropsContext } from '../../contexts/previewModal/PropsContext';
import { defaultCrmObjectPreviewModalContextProps } from '../../contexts/previewModal/constants';
import { PreviewModalContext, PreviewModalContextProvider } from '../../contexts/previewModal/context';
import { PropertiesContext } from '../../contexts/properties/context';
import PropertiesContextHOC from '../../hoc/PropertiesContextHOC';
import PreviewModal from './PreviewModal';
import { jsx as _jsx } from "react/jsx-runtime";
function CrmObjectPreviewModal() {
  const {
    objectEntity,
    previewModalActions
  } = useContext(PreviewModalContext);
  const {
    portalId,
    openUrl,
    usageTracker,
    activeObjectId,
    onClose,
    crmObjectType
  } = useContext(PreviewModalPropsContext);
  const {
    propertiesActions,
    searchProperties,
    arePropertiesLoaded,
    properties,
    externalOptions,
    mentionOwners
  } = useContext(PropertiesContext);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isApiOutageError, setIsApiOutageError] = useState(false);
  const propertiesLoaded = useMemo(() => _get(arePropertiesLoaded, `${crmObjectType}`, false), [crmObjectType, arePropertiesLoaded]);
  const objectTypeProperties = useMemo(() => _get(properties, `${crmObjectType}`, {
    associationCardProperties: [],
    defaultSidebarProperties: [],
    detailedPropertiesInformation: {}
  }), [crmObjectType, properties]);
  const isExternalOptionFetched = useRef(false);
  const openObjectDirectLink = (event, url) => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS[`associated${_capitalize(crmObjectType)}`]
    }, ACTIONS.associatedContact.clickOpen));
    event.preventDefault();
    event.stopPropagation();
    openUrl(url);
  };
  useEffect(() => {
    if (activeObjectId && !propertiesLoaded) {
      setIsPreviewLoading(true);
      propertiesActions.getProperties({
        objectType: crmObjectType,
        errorCallback: () => setIsApiOutageError(true)
      });
    }
  }, [propertiesLoaded, activeObjectId, crmObjectType, propertiesActions]);
  useEffect(() => {
    if (activeObjectId && propertiesLoaded) {
      setIsPreviewLoading(true);
      previewModalActions.getActiveObjectEntity({
        cb: () => setIsPreviewLoading(false),
        objectType: singularTypeToPlural[crmObjectType],
        itemId: activeObjectId,
        associationCardProperties: objectTypeProperties.associationCardProperties,
        defaultSidebarProperties: objectTypeProperties.defaultSidebarProperties
      });
    }
  }, [activeObjectId, propertiesLoaded, properties, objectTypeProperties, crmObjectType, previewModalActions]);

  // we need owners if proeprty has html type (rte editor)
  // or reference type is OWNER
  useEffect(() => {
    const searchProps = _get(searchProperties, `${crmObjectType}`, []);
    const htmlProperty = searchProps.find(property => property.fieldType === propertyFieldTypes.html);
    const ownerProperty = searchProps.find(property => property.referenceType === propertyReferenceTypes.hubspot_owner_id || property.referencedObjectType === propertyReferenceTypes.hubspot_owner_id);
    const fetchedExternadOptions = externalOptions[crmObjectType][propertyReferenceTypes.hubspot_owner_id];
    if (fetchedExternadOptions && fetchedExternadOptions.hasMore === false && fetchedExternadOptions.results) return;
    if (htmlProperty && !ownerProperty) {
      propertiesActions.getExternalOptions({
        query: '',
        referenceType: propertyReferenceTypes.hubspot_owner_id,
        offset: 0,
        limit: PROPERTIES_LIMIT,
        crmObjectType,
        externalOptions,
        mentionOwners
      });
    }
  }, [searchProperties, crmObjectType, externalOptions, propertiesActions, mentionOwners]);

  // fetch external options to show labels instead of values
  useEffect(() => {
    const objectCardProperties = properties[crmObjectType].defaultSidebarProperties;
    const objectProperties = searchProperties[crmObjectType].filter(item => objectCardProperties.includes(item.name) && item.externalOptions);
    if (!isExternalOptionFetched.current && objectProperties.length) {
      // need to prevent infinite loop
      isExternalOptionFetched.current = true;
      objectProperties.forEach(property => {
        const hardcodedRefType = _get(PROPERTY_NAME_TO_REFERENCE_TYPE[crmObjectType], property.name, '');
        const referenceType = property.referenceType || property.referencedObjectType || hardcodedRefType || '';
        const fetchedExternadOptions = externalOptions[crmObjectType][referenceType];
        const readOnly = _get(property, 'modificationMetadata.readOnlyValue', false);
        if ((!fetchedExternadOptions || fetchedExternadOptions.hasMore !== false) && !readOnly) {
          propertiesActions.getExternalOptions({
            query: '',
            referenceType,
            offset: fetchedExternadOptions && fetchedExternadOptions.offset ? Number(fetchedExternadOptions.offset) : 0,
            limit: PROPERTIES_LIMIT,
            typeId: pluralTypeToId[singularTypeToPlural[crmObjectType]],
            crmObjectType,
            externalOptions,
            mentionOwners
          });
        }
      });
    }
  }, [crmObjectType, externalOptions, mentionOwners, properties, propertiesActions, searchProperties]);
  useEffect(() => {
    if (!activeObjectId) {
      previewModalActions.resetObjectEntity();
      setIsPreviewLoading(false);
      setIsApiOutageError(false);
    }
  }, [activeObjectId, previewModalActions]);
  return activeObjectId ? /*#__PURE__*/_jsx(PreviewModal, {
    object: objectEntity || {
      id: '',
      properties: {}
    },
    portalId: portalId,
    type: singularTypeToPlural[crmObjectType],
    openObjectDirectLink: openObjectDirectLink,
    propertiesDetails: objectTypeProperties.detailedPropertiesInformation,
    propertyKeysToShow: objectTypeProperties.defaultSidebarProperties,
    isLoading: isPreviewLoading,
    closeFunction: onClose,
    isApiOutageError: isApiOutageError
  }) : null;
}
const WrappedCrmObjectPreviewModal = props => {
  const definedProps = _omitBy(props, _isNil);
  const apiWrapper = _omitBy(props.apiWrapper, _isNil);
  const properties = Object.assign({}, defaultCrmObjectPreviewModalContextProps, definedProps, {
    apiWrapper: Object.assign({}, defaultCrmObjectPreviewModalContextProps.apiWrapper, apiWrapper)
  });
  return /*#__PURE__*/_jsx(PreviewModalPropsContext.Provider, {
    value: properties,
    children: /*#__PURE__*/_jsx(PreviewModalContextProvider, {
      children: /*#__PURE__*/_jsx(PropertiesContextHOC, {
        apiWrapper: {
          fetchExternalOptions: properties.apiWrapper.fetchExternalOptions,
          validateProperty: properties.apiWrapper.validateProperty,
          fetchCardProperties: properties.apiWrapper.fetchCardProperties,
          fetchPropertiesDetails: properties.apiWrapper.fetchPropertiesDetails
        },
        children: /*#__PURE__*/_jsx(CrmObjectPreviewModal, {})
      })
    })
  });
};
export default WrappedCrmObjectPreviewModal;